/**
 * useRealtimeNotifications - General system notification streaming via WebSocket
 * Streams system alerts, announcements, status changes, and user notifications
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useWebSocket } from '@/domains/market-data/hooks/useWebSocket';

// =============================================================================
// TYPES
// =============================================================================

export type NotificationType =
  | 'system'
  | 'trade'
  | 'risk'
  | 'ai_signal'
  | 'arbitrage'
  | 'announcement'
  | 'maintenance'
  | 'error';

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface RealtimeNotification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  data?: Record<string, any>;
  source: string;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  expiresAt?: string;
  timestamp: string;
}

export interface SystemStatus {
  service: string;
  status: 'healthy' | 'degraded' | 'down' | 'maintenance';
  message?: string;
  latency?: number;
  lastCheck: string;
}

export interface UseRealtimeNotificationsConfig {
  /** Auto-connect on mount */
  autoConnect?: boolean;
  /** Maximum notifications to buffer */
  maxNotifications?: number;
  /** Notification types to filter */
  types?: NotificationType[];
  /** Minimum priority */
  minPriority?: NotificationPriority;
  /** Callback on new notification */
  onNotification?: (notification: RealtimeNotification) => void;
  /** Callback on system status change */
  onSystemStatus?: (status: SystemStatus) => void;
  /** Show browser notifications for high priority */
  browserNotifications?: boolean;
}

// =============================================================================
// PRIORITY ORDERING
// =============================================================================

const PRIORITY_ORDER: Record<NotificationPriority, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};

// =============================================================================
// HOOK
// =============================================================================

export const useRealtimeNotifications = (config: UseRealtimeNotificationsConfig = {}) => {
  const {
    autoConnect = true,
    maxNotifications = 100,
    types,
    minPriority = 'low',
    onNotification,
    onSystemStatus,
    browserNotifications = false,
  } = config;

  const [notifications, setNotifications] = useState<RealtimeNotification[]>([]);
  const [systemStatuses, setSystemStatuses] = useState<Map<string, SystemStatus>>(new Map());
  const [unreadCount, setUnreadCount] = useState(0);

  const callbacksRef = useRef({ onNotification, onSystemStatus });
  callbacksRef.current = { onNotification, onSystemStatus };

  // Request browser notification permissions
  useEffect(() => {
    if (browserNotifications && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { });
    }
  }, [browserNotifications]);

  const showBrowserNotification = useCallback((notification: RealtimeNotification) => {
    if (
      browserNotifications &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted' &&
      PRIORITY_ORDER[notification.priority] >= PRIORITY_ORDER.high
    ) {
      try {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/favicon.ico',
          tag: notification.id,
        });
      } catch {
        // Browser notifications not available
      }
    }
  }, [browserNotifications]);

  const handleMessage = useCallback((data: any) => {
    if (data.type === 'pong' || data.type === 'ping') return;

    switch (data.type) {
      case 'notification': {
        const notification = data.data as RealtimeNotification;

        // Filter by types
        if (types?.length && !types.includes(notification.type)) return;

        // Filter by priority
        if (PRIORITY_ORDER[notification.priority] < PRIORITY_ORDER[minPriority]) return;

        // Check expiry
        if (notification.expiresAt && new Date(notification.expiresAt) < new Date()) return;

        setNotifications((prev: RealtimeNotification[]) => [notification, ...prev].slice(0, maxNotifications));
        if (!notification.read) {
          setUnreadCount((prev: number) => prev + 1);
        }

        showBrowserNotification(notification);
        callbacksRef.current.onNotification?.(notification);
        break;
      }

      case 'system_status': {
        const status = data.data as SystemStatus;
        setSystemStatuses((prev: Map<string, SystemStatus>) => {
          const updated = new Map(prev);
          updated.set(status.service, status);
          return updated;
        });
        callbacksRef.current.onSystemStatus?.(status);
        break;
      }
    }
  }, [types, minPriority, maxNotifications, showBrowserNotification]);

  // Private channel: mint short-lived ticket on every connect/reconnect
  const { state, send, connect, disconnect, subscribe } = useWebSocket(
    {
      channel: 'notifications',
      useTicket: true,
      requireTicket: true,
      softFailAuth: false,
      reconnect: { enabled: true, maxAttempts: 10, delay: 1000, backoffMultiplier: 2 },
      heartbeat: { enabled: true, interval: 30000, message: { type: 'ping' } },
    },
    handleMessage
  );

  // Auto-connect
  useEffect(() => {
    if (autoConnect) {
      connect();
    }
    return () => disconnect();
  }, [autoConnect, connect, disconnect]);

  // Subscribe to notification channels when connected
  useEffect(() => {
    if (state.status === 'connected') {
      subscribe('notification', () => { });
      subscribe('system_status', () => { });

      send({
        type: 'subscribe',
        channels: ['notifications', 'system_status'],
      });
    }
  }, [state.status, subscribe, send]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId: string) => {
    setNotifications((prev: RealtimeNotification[]) =>
      prev.map((n: RealtimeNotification) => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount((prev: number) => Math.max(0, prev - 1));
    send({ type: 'mark_read', notificationId });
  }, [send]);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications((prev: RealtimeNotification[]) => prev.map((n: RealtimeNotification) => ({ ...n, read: true })));
    setUnreadCount(0);
    send({ type: 'mark_all_read' });
  }, [send]);

  // Dismiss notification
  const dismissNotification = useCallback((notificationId: string) => {
    setNotifications((prev: RealtimeNotification[]) => {
      const notification = prev.find((n: RealtimeNotification) => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount((c: number) => Math.max(0, c - 1));
      }
      return prev.filter((n: RealtimeNotification) => n.id !== notificationId);
    });
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Derived state
  const statusesArray = Array.from(systemStatuses.values()) as SystemStatus[];
  const unhealthyServices = statusesArray.filter((s: SystemStatus) => s.status !== 'healthy');
  const urgentNotifications = notifications.filter((n: RealtimeNotification) => n.priority === 'urgent');
  const unreadNotifications = notifications.filter((n: RealtimeNotification) => !n.read);

  return {
    // Connection
    connectionStatus: state.status,
    isConnected: state.status === 'connected',
    connect,
    disconnect,

    // Data
    notifications,
    unreadNotifications,
    urgentNotifications,
    unreadCount,
    systemStatuses: statusesArray,
    unhealthyServices,

    // Actions
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    send,
  };
};

export default useRealtimeNotifications;
